import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data, status } = await api.post("/auth/session", { session_id: sessionId });
        if (status === 202 || data?.pending) {
          // Access request queued — no session issued.
          window.history.replaceState(null, "", "/login");
          navigate("/login", { replace: true, state: { pending: true, email: data?.email, message: data?.message } });
          return;
        }
        setUser(data.user);
        // strip hash & route to the persona chooser (shown after every login)
        window.history.replaceState(null, "", "/persona");
        navigate("/persona", { replace: true, state: { user: data.user } });
      } catch (e) {
        logError("auth/session failed", e);
        const detail = e?.response?.data?.detail;
        const isDomainReject = e?.response?.status === 403;
        window.history.replaceState(null, "", "/login");
        navigate("/login", { replace: true, state: isDomainReject ? { rejected: true, message: detail } : {} });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-paper">
      <div className="text-center">
        <div className="font-display text-2xl tracking-tight text-brand-ink">Authenticating…</div>
        <div className="font-mono-tech text-xs text-zinc-500 mt-2 uppercase tracking-widest">Establishing secure session</div>
      </div>
    </div>
  );
}
